/**
 * The AMTC call-centre number, and whether it is safe to dial.
 *
 * The real number is configured (owner-supplied, 2026-08-31) and lives in
 * `env.ts`. This module is what every "call the AMTC team" button asks before
 * rendering a `tel:` link, and it exists because of what was true until that
 * day rather than as decoration.
 *
 * WHAT WENT WRONG. `env.amtcPhone` used to default to `+233302000000`, a
 * stand-in nobody had replaced: `VITE_AMTC_PHONE` was set in no environment —
 * not `infra/staging/`, not the deploy workflow, not `.env.example`. Verified in
 * the live staging bundle on 2026-08-31, that placeholder was the only AMTC
 * number shipped, so the ambulance screen and the appointment fallback both
 * offered patients a Call button that dialled nothing.
 *
 * `dialablePhone` could not catch it. The placeholder is nine digits after the
 * country code with no letters in it, so it passes every shape check that
 * rejects `'+233-XX-XXX-XXXX'`. It looked exactly like a real Accra landline.
 * Shape is not enough; the value has to be checked by identity.
 *
 * A dead call button is worse than no call button — it costs the patient the
 * attempt before they look for another route, and on the ambulance screen that
 * attempt is the whole point. That reasoning is already written into
 * `lib/phone.ts`; this applies it to the one number that comes from config
 * rather than from the database.
 */
import { env } from './env'
import { dialablePhone } from './phone'

/** The old build-time default. Kept so a revert to it cannot silently ship a
 * dead button again — the guard is the regression test, not the history. */
export const PLACEHOLDER_AMTC_PHONE = '+233302000000'

/** The number as it should be READ — spaced, the way it is written down. */
export function amtcDisplayNumber(): string | null {
  const configured = env.amtcPhone.trim()
  if (configured.length === 0) return null
  if (configured.replace(/\s/g, '') === PLACEHOLDER_AMTC_PHONE) return null
  return configured
}

/** The number as it should be DIALLED — separators stripped, or null when it is
 * absent, a placeholder, or not a real number's shape. */
export function amtcDialNumber(): string | null {
  const display = amtcDisplayNumber()
  return display === null ? null : dialablePhone(display)
}
